import { useCallback, useEffect, useState } from 'react';
import { type FundFlow, groupTint, type WalletProfile } from '@pleco/shared';
import { fmtAge, fmtNum, fmtPct, fmtPrice, fmtSol, fmtUsd, shortAddr, solscan, solscanTx } from '../../lib/fmt.js';
import { terminalApi } from '../../lib/terminalApi.js';
import { groupLabel, useTerminal } from '../../store/terminalStore.js';
import { FundFlowDiagram } from './FundFlowDiagram.js';
import { TagChips } from './Bits.js';

/**
 * Trader detail + fund flow for one wallet. Opened from a bubble, a trade,
 * a holder row or a group; clicking a node in the flow pushes that wallet
 * onto the breadcrumb stack.
 */
export function WalletDrawer({ mint, supply, onLocate }: { mint: string; supply: number; onLocate: (address: string) => void }) {
  const { drawer, pushWallet, backWallet, closeWallet, openWallet, analysis, solUsd, selectGroup } = useTerminal();
  const address = drawer.at(-1);
  const [profile, setProfile] = useState<WalletProfile>();
  const [flow, setFlow] = useState<FundFlow>();
  const [err, setErr] = useState<string>();
  const [flowErr, setFlowErr] = useState<string>();
  const [tab, setTab] = useState<'flow' | 'trades'>('flow');
  const [copied, setCopied] = useState(false);
  // lives here, not in FundFlowDiagram, so clicking a node to follow the
  // money to a new wallet (which remounts the diagram) keeps the view open
  const [flowFull, setFlowFull] = useState(false);
  const toggleFlowFull = useCallback(() => setFlowFull((f) => !f), []);

  useEffect(() => {
    if (!address) return;
    let alive = true;
    setProfile(undefined);
    // keep the previous flow on screen (dimmed) until the next one arrives,
    // so following the money hop by hop never blanks/remounts the diagram
    setErr(undefined);
    setFlowErr(undefined);
    terminalApi
      .wallet(mint, address)
      .then((p) => alive && setProfile(p))
      .catch((e) => alive && setErr(e instanceof Error ? e.message : 'Could not load wallet'));
    terminalApi
      .flow(mint, address)
      .then((f) => alive && setFlow(f))
      .catch((e) => alive && setFlowErr(e instanceof Error ? e.message : 'Could not load fund flow'));
    return () => {
      alive = false;
    };
  }, [mint, address]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeWallet();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closeWallet]);

  if (!address) return null;
  const pos = profile?.position;
  const pnl = pos ? pos.realizedPnlSol + pos.unrealizedPnlSol : 0;
  const g = profile?.group;
  const usd = (sol?: number) => (sol !== undefined && solUsd ? fmtUsd(sol * solUsd) : '');
  const entryMc = pos?.avgBuyPriceSol && solUsd ? pos.avgBuyPriceSol * solUsd * supply : undefined;
  const exitMc = pos?.avgSellPriceSol && solUsd ? pos.avgSellPriceSol * solUsd * supply : undefined;

  return (
    <div className="wdrawer" role="dialog" aria-label="Wallet detail">
      <div className="wd-head">
        {drawer.length > 1 && (
          <div className="wd-crumbs">
            {drawer.map((a, i) => (
              <span key={`${a}-${i}`}>
                {i > 0 && <span className="faint"> → </span>}
                <button
                  className={`linkish mono ${i === drawer.length - 1 ? 'cur' : ''}`}
                  onClick={() => {
                    // jump back to that point of the trail
                    for (let k = drawer.length - 1; k > i; k--) backWallet();
                  }}
                >
                  {shortAddr(a)}
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="wd-title">
          {drawer.length > 1 && (
            <button className="iconbtn" onClick={backWallet} title="Back">
              ←
            </button>
          )}
          <div className="wd-addr">
            <div className="mono">
              {profile?.label && <span className="wd-label">{profile.label}</span>}
              {shortAddr(address, 6, 6)}
            </div>
            <div className="wd-links">
              <button
                className="linkish"
                onClick={() => {
                  void navigator.clipboard.writeText(address);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1200);
                }}
              >
                {copied ? 'Copied ✓' : 'Copy address'}
              </button>
              <a className="linkish" href={solscan(address)} target="_blank" rel="noreferrer">
                Solscan ↗
              </a>
              <button className="linkish" onClick={() => onLocate(address)}>
                Show on map
              </button>
            </div>
          </div>
          <span className="grow" />
          <button className="iconbtn" onClick={closeWallet} title="Close (Esc)">
            ✕
          </button>
        </div>
        {profile && <TagChips tags={profile.info.tags} full />}
      </div>

      <div className="wd-body">
        {err && <div className="tempty">{err}</div>}
        {!profile && !err && (
          <div className="tempty">
            <span className="spinner" /> Loading wallet…
          </div>
        )}

        {profile && pos && (
          <>
            <div className="wd-stats">
              <div>
                <em>Holding</em>
                <b>{fmtNum(pos.balance)}</b>
                <span className="faint">{fmtPct(pos.holdingPercent)} of supply</span>
              </div>
              <div>
                <em>Profit on this token</em>
                <b className={pnl > 0 ? 'buy' : pnl < 0 ? 'sell' : ''}>{fmtSol(pnl, true)} SOL</b>
                <span className="faint">
                  {fmtSol(pos.realizedPnlSol, true)} taken · {fmtSol(pos.unrealizedPnlSol, true)} on paper
                </span>
              </div>
              <div>
                <em>Bought</em>
                <b className="buy">{fmtSol(pos.solSpent)} SOL</b>
                <span className="faint">
                  {pos.buys} buys {usd(pos.solSpent) && `· ${usd(pos.solSpent)}`}
                </span>
              </div>
              <div>
                <em>Sold</em>
                <b className="sell">{fmtSol(pos.solReceived)} SOL</b>
                <span className="faint">
                  {pos.sells} sells {usd(pos.solReceived) && `· ${usd(pos.solReceived)}`}
                </span>
              </div>
              <div>
                <em>Bought at market cap</em>
                <b>{fmtUsd(entryMc)}</b>
                <span className="faint">sold at {fmtUsd(exitMc)}</span>
              </div>
              <div>
                <em>Wallet balance</em>
                <b>{fmtSol(profile.solBalance)} SOL</b>
                <span className="faint">
                  age {fmtAge(profile.firstActivity)} {profile.txCountLowerBound ? `· ${profile.txCountLowerBound >= 1000 ? '1000+' : profile.txCountLowerBound} tx` : ''}
                </span>
              </div>
            </div>

            <div className="wd-chain">
              <em>Who funded this wallet</em>
              <div>
                {profile.info.funderOfFunder && (
                  <>
                    <button className="linkish mono" onClick={() => pushWallet(profile.info.funderOfFunder!)}>
                      {shortAddr(profile.info.funderOfFunder)}
                    </button>
                    <span className="faint"> → </span>
                  </>
                )}
                {profile.info.funder ? (
                  <button className="linkish mono" onClick={() => pushWallet(profile.info.funder!)}>
                    {profile.info.funderLabel ?? shortAddr(profile.info.funder)}
                  </button>
                ) : (
                  <span className="faint">unknown</span>
                )}
                <span className="faint"> → </span>
                <span className="mono">this wallet</span>
                {profile.firstActivity && <span className="faint"> · funded {fmtAge(profile.firstActivity)} ago</span>}
              </div>
            </div>

            {g && (
              <div className="wd-group" style={{ borderColor: groupTint(g) }}>
                <div className="row">
                  <span className="gbadge" style={{ background: groupTint(g) }}>
                    {groupLabel(g)}
                  </span>
                  <b>{g.name}</b>
                  <span className="faint">
                    {g.wallets.length} wallets · holds {fmtPct(g.stats.holdingPercent)} · {g.confidence}% confidence
                  </span>
                  <span className="grow" />
                  <button className="linkish" onClick={() => selectGroup(g.id)}>
                    highlight group
                  </button>
                </div>
                <div className="faint" style={{ fontSize: 11.5, marginTop: 4 }}>
                  {g.evidence[0]?.detail}
                </div>
                <div className="gwallets" style={{ marginTop: 6 }}>
                  {g.wallets
                    .filter((w) => w !== address)
                    .slice(0, 12)
                    .map((w) => (
                      <button key={w} className="gw mono" onClick={() => openWallet(w)}>
                        {shortAddr(w)}
                      </button>
                    ))}
                </div>
              </div>
            )}
          </>
        )}

        <div className="tabs" style={{ marginTop: 10 }}>
          <button className={tab === 'flow' ? 'active' : ''} onClick={() => setTab('flow')}>
            Fund flow
          </button>
          <button className={tab === 'trades' ? 'active' : ''} onClick={() => setTab('trades')}>
            Trades {profile ? `(${profile.trades.length})` : ''}
          </button>
        </div>

        {tab === 'flow' &&
          (flowErr ? (
            <div className="tempty">{flowErr}</div>
          ) : (
            <>
              {flow && flow.focus === address && (
                <div className="wd-flowsum faint">
                  Received <b className="buy">{fmtSol(flow.summary.inSol)} SOL</b> from wallets · sent <b className="sell">{fmtSol(flow.summary.outSol)} SOL</b> to
                  wallets. Click any box to follow the money from there.
                </div>
              )}
              <FundFlowDiagram
                flow={flow}
                loading={!flow || flow.focus !== address}
                focus={address}
                analysis={analysis}
                onOpen={pushWallet}
                full={flowFull}
                onToggleFull={toggleFlowFull}
              />
            </>
          ))}

        {tab === 'trades' && profile && (
          <table className="ttable">
            <thead>
              <tr>
                <th>Age</th>
                <th>Type</th>
                <th className="r">MC</th>
                <th className="r">Amount</th>
                <th className="r">SOL</th>
              </tr>
            </thead>
            <tbody>
              {profile.trades.map((t) => (
                <tr key={`${t.signature}:${t.idx}`}>
                  <td className="faint">
                    <a href={solscanTx(t.signature)} target="_blank" rel="noreferrer">
                      {fmtAge(t.ts)}
                    </a>
                  </td>
                  <td className={t.side}>{t.side === 'buy' ? 'Buy' : 'Sell'}</td>
                  <td className="r mono">{t.priceUsd ? fmtUsd(t.priceUsd * supply) : fmtPrice(undefined)}</td>
                  <td className="r mono">{fmtNum(t.tokenAmount)}</td>
                  <td className={`r mono ${t.side}`}>{fmtSol(t.solAmount)}</td>
                </tr>
              ))}
              {!profile.trades.length && (
                <tr>
                  <td colSpan={5} className="tempty">
                    No trades of this token — it may have received tokens by transfer.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
